/**
 * TypeScript interfaces for OAuth Vault Integration JSONB structures
 *
 * These interfaces define the shape of data stored in JSONB fields across the unified
 * user_connections table and platform-specific tables. They ensure type safety when
 * extracting data from JSONB fields and provide clear documentation of data structures.
 */

// Base interfaces for common JSONB field structures
export interface BaseConnectionMetadata {
  vault_id?: string;
  access_token_vault_id?: string;
  refresh_token_vault_id?: string;
  token_expires_at?: string;
  accounts_created?: number;
  connection_id?: string;
  platform_table_id?: string;
  scopes?: string;
  oauth_version?: string;
  created_via?: 'oauth_callback' | 'manual' | 'migration';
  last_token_refresh?: string;
}

export interface BaseConnectionData {
  created_at?: string;
  updated_at?: string;
  last_sync_type?: string;
  error_count?: number;
  last_error?: string;
  last_successful_sync?: string;
}

// Platform-specific metadata interfaces
export interface ShopifyConnectionMetadata extends BaseConnectionMetadata {
  shop_name?: string;
  shop_id?: number;
  myshopify_domain?: string;
  plan_name?: string;
  plan_display_name?: string;
  shop_owner?: string;
  email?: string;
}

export interface ShopifyConnectionData extends BaseConnectionData {
  shop_name?: string;
  domain?: string;
  myshopify_domain?: string;
  email?: string;
  currency?: string;
  timezone?: string;
  country?: string;
  address?: {
    address1?: string;
    city?: string;
    province?: string;
    country?: string;
    zip?: string;
  };
  phone?: string;
  description?: string;
  products?: Array<{
    id: string;
    title: string;
    product_type?: string;
    tags?: string;
    vendor?: string;
  }>;
}

export interface FacebookConnectionMetadata extends BaseConnectionMetadata {
  page_id?: string;
  page_name?: string;
  page_access_token_vault_id?: string;
  user_access_token_vault_id?: string;
  permissions_granted?: string[];
}

export interface FacebookConnectionData extends BaseConnectionData {
  page_id?: string;
  page_name?: string;
  page_username?: string;
  page_category?: string;
  followers_count?: number;
  fan_count?: number;
  picture_url?: string;
  profile_picture_url?: string;
  cover_url?: string;
  about?: string;
  website?: string;
  phone?: string;
  emails?: string[];
  location?: {
    street?: string;
    city?: string;
    state?: string;
    country?: string;
    zip?: string;
  };
}

export interface InstagramConnectionMetadata extends BaseConnectionMetadata {
  instagram_business_account_id?: string;
  facebook_page_id?: string;
  account_type?: 'BUSINESS' | 'CREATOR' | 'PERSONAL';
}

export interface InstagramConnectionData extends BaseConnectionData {
  username?: string;
  name?: string;
  biography?: string;
  website?: string;
  followers_count?: number;
  follows_count?: number;
  media_count?: number;
  profile_picture_url?: string;
  account_type?: 'BUSINESS' | 'CREATOR' | 'PERSONAL';
  business_discovery_enabled?: boolean;
}

export interface LinkedInConnectionMetadata extends BaseConnectionMetadata {
  organization_id?: string;
  organization_name?: string;
  person_urn?: string;
  organization_urn?: string;
}

export interface LinkedInConnectionData extends BaseConnectionData {
  organization_id?: string;
  organization_name?: string;
  description?: string;
  website?: string;
  industry?: string;
  company_size?: string;
  headquarters?: {
    country?: string;
    geographic_area?: string;
    city?: string;
  };
  logo_url?: string;
  profile_picture_url?: string;
  cover_image_url?: string;
  follower_count?: number;
  employee_count?: number;
  vanity_name?: string;
  credential_subject?: string;
  linkedin_person_id?: string;
  linkedin_urn?: string;
  identity_state?: string;
}

/** Separate read-only advertising credential; never organic LinkedIn data. */
export interface LinkedInAdsConnectionData extends BaseConnectionData {
  ad_account_id?: string;
  ad_account_name?: string;
  currency?: string;
  status?: string;
  account_role?: string;
  test?: boolean;
}

export interface TikTokConnectionMetadata extends BaseConnectionMetadata {
  open_id?: string;
  business_id?: string;
  advertiser_id?: string;
  account_type?: 'BUSINESS' | 'CREATOR';
}

export interface TikTokConnectionData extends BaseConnectionData {
  display_name?: string;
  username?: string;
  avatar_url?: string;
  bio?: string;
  follower_count?: number;
  following_count?: number;
  likes_count?: number;
  video_count?: number;
  profile_deep_link?: string;
  is_verified?: boolean;
}

export interface GoogleAnalyticsConnectionMetadata extends BaseConnectionMetadata {
  property_id?: string;
  property_name?: string;
  account_id?: string;
  account_name?: string;
  measurement_id?: string;
}

export interface GoogleAnalyticsConnectionData extends BaseConnectionData {
  property_id?: string;
  property_name?: string;
  account_id?: string;
  account_name?: string;
  website_url?: string;
  industry_category?: string;
  time_zone?: string;
  currency_code?: string;
  data_retention_settings?: {
    event_data_retention?: string;
    reset_user_data_on_new_activity?: boolean;
  };
}

export interface GoogleAdsConnectionData extends BaseConnectionData {
  customer_id?: string;
  customer_name?: string;
  account_id?: string;
  account_name?: string;
  currency?: string;
}

/**
 * The non-secret identity fields persisted by the Meta Ads picker. A Meta
 * connection can have many selected `meta_ad_account` resources; this shape
 * describes the credential connection itself, not a fabricated account list.
 */
export interface MetaAdsConnectionData extends BaseConnectionData {
  ad_account_id?: string;
  name?: string;
  business_name?: string;
  currency?: string;
}

export interface YouTubeConnectionMetadata extends BaseConnectionMetadata {
  channel_id?: string;
  channel_title?: string;
  custom_url?: string;
  country?: string;
  subscriber_count?: number;
  video_count?: number;
  view_count?: number;
  uploads_playlist_id?: string;
}

export interface YouTubeConnectionData extends BaseConnectionData {
  channel_id?: string;
  channel_title?: string;
  custom_url?: string;
  description?: string;
  profile_image_url?: string;
  country?: string;
  subscriber_count?: number;
  video_count?: number;
  view_count?: number;
  uploads_playlist_id?: string;
  profile_url?: string;
}

export interface PinterestConnectionMetadata extends BaseConnectionMetadata {
  pinterest_user_id?: string;
  username?: string | null;
  display_name?: string | null;
  profile_image_url?: string | null;
  account_type?: string | null;
  website_url?: string | null;
  default_board_id?: string | null;
  board_count?: number;
  pin_count?: number;
  follower_count?: number;
  refresh_token_expires_at?: string | null;
}

export interface PinterestConnectionData extends BaseConnectionData {
  pinterest_user_id?: string;
  username?: string | null;
  display_name?: string | null;
  profile_image_url?: string | null;
  account_type?: string | null;
  website_url?: string | null;
  default_board_id?: string | null;
  board_count?: number;
  pin_count?: number;
  follower_count?: number;
}

// Union types for dynamic metadata/connection_data access
export type PlatformConnectionMetadata =
  | ShopifyConnectionMetadata
  | FacebookConnectionMetadata
  | InstagramConnectionMetadata
  | LinkedInConnectionMetadata
  | TikTokConnectionMetadata
  | GoogleAnalyticsConnectionMetadata
  | YouTubeConnectionMetadata
  | PinterestConnectionMetadata;

export type PlatformConnectionData =
  | ShopifyConnectionData
  | FacebookConnectionData
  | InstagramConnectionData
  | LinkedInConnectionData
  | LinkedInAdsConnectionData
  | TikTokConnectionData
  | GoogleAnalyticsConnectionData
  | GoogleAdsConnectionData
  | MetaAdsConnectionData
  | YouTubeConnectionData
  | PinterestConnectionData;

// Helper type for the complete user_connections row
export interface UserConnection {
  id: string;
  user_id: string;
  platform:
    | 'shopify'
    | 'facebook'
    | 'instagram'
    | 'meta-ads'
    | 'google-ads'
    | 'linkedin'
    | 'linkedin-ads'
    | 'tiktok'
    | 'google_analytics'
    | 'google-analytics'
    | 'youtube'
    | 'pinterest';
  platform_user_id: string;
  is_active: boolean;
  connected_at: string;
  last_sync?: string;
  metadata?: PlatformConnectionMetadata;
  connection_data?: PlatformConnectionData;
  created_at: string;
  updated_at: string;
}

// Type guards for runtime type checking
export function isShopifyMetadata(metadata: any): metadata is ShopifyConnectionMetadata {
  return metadata && (metadata.shop_name !== undefined || metadata.myshopify_domain !== undefined);
}

export function isFacebookMetadata(metadata: any): metadata is FacebookConnectionMetadata {
  return metadata && (metadata.page_id !== undefined || metadata.page_name !== undefined);
}

export function isInstagramMetadata(metadata: any): metadata is InstagramConnectionMetadata {
  return (
    metadata &&
    (metadata.instagram_business_account_id !== undefined || metadata.account_type !== undefined)
  );
}

export function isLinkedInMetadata(metadata: any): metadata is LinkedInConnectionMetadata {
  return (
    metadata && (metadata.organization_id !== undefined || metadata.organization_urn !== undefined)
  );
}

export function isTikTokMetadata(metadata: any): metadata is TikTokConnectionMetadata {
  return metadata && (metadata.open_id !== undefined || metadata.business_id !== undefined);
}

export function isGoogleAnalyticsMetadata(
  metadata: any,
): metadata is GoogleAnalyticsConnectionMetadata {
  return metadata && (metadata.property_id !== undefined || metadata.account_id !== undefined);
}

// Utility functions for safe JSONB extraction
export function extractMetadata<T extends PlatformConnectionMetadata>(
  data: any,
  platform: string,
): T {
  const metadata = (data?.metadata as any) || {};

  // Provide platform-specific defaults with token vault support
  switch (platform) {
    case 'shopify':
      return {
        shop_name: metadata.shop_name || '',
        scopes: metadata.scopes || '',
        vault_id: metadata.vault_id,
        access_token_vault_id: metadata.access_token_vault_id,
        refresh_token_vault_id: metadata.refresh_token_vault_id,
        token_expires_at: metadata.token_expires_at,
        ...metadata,
      } as T;

    case 'facebook':
      return {
        page_name: metadata.page_name || '',
        page_id: metadata.page_id || '',
        scopes: metadata.scopes || '',
        vault_id: metadata.vault_id,
        access_token_vault_id:
          metadata.access_token_vault_id || metadata.page_access_token_vault_id,
        refresh_token_vault_id: metadata.refresh_token_vault_id,
        token_expires_at: metadata.token_expires_at,
        page_access_token_vault_id: metadata.page_access_token_vault_id,
        user_access_token_vault_id: metadata.user_access_token_vault_id,
        ...metadata,
      } as T;

    case 'instagram':
      return {
        account_type: metadata.account_type || 'BUSINESS',
        scopes: metadata.scopes || '',
        vault_id: metadata.vault_id,
        access_token_vault_id: metadata.access_token_vault_id,
        refresh_token_vault_id: metadata.refresh_token_vault_id,
        token_expires_at: metadata.token_expires_at,
        ...metadata,
      } as T;

    case 'linkedin':
      return {
        organization_name: metadata.organization_name || '',
        scopes: metadata.scopes || '',
        vault_id: metadata.vault_id,
        access_token_vault_id: metadata.access_token_vault_id,
        refresh_token_vault_id: metadata.refresh_token_vault_id,
        token_expires_at: metadata.token_expires_at,
        ...metadata,
      } as T;

    case 'linkedin-ads':
      return {
        ad_account_id: metadata.ad_account_id || '',
        ad_account_name: metadata.ad_account_name || '',
        currency: metadata.currency || '',
        scopes: metadata.scopes || '',
        vault_id: metadata.vault_id,
        access_token_vault_id: metadata.access_token_vault_id,
        refresh_token_vault_id: metadata.refresh_token_vault_id,
        token_expires_at: metadata.token_expires_at,
        ...metadata,
      } as T;

    case 'tiktok':
      return {
        account_type: metadata.account_type || 'BUSINESS',
        scopes: metadata.scopes || '',
        vault_id: metadata.vault_id,
        access_token_vault_id: metadata.access_token_vault_id,
        refresh_token_vault_id: metadata.refresh_token_vault_id,
        token_expires_at: metadata.token_expires_at,
        ...metadata,
      } as T;

    case 'google_analytics':
    case 'google-analytics':
      return {
        property_name: metadata.property_name || '',
        account_name: metadata.account_name || '',
        scopes: metadata.scopes || '',
        vault_id: metadata.vault_id,
        access_token_vault_id: metadata.access_token_vault_id,
        refresh_token_vault_id: metadata.refresh_token_vault_id,
        token_expires_at: metadata.token_expires_at,
        ...metadata,
      } as T;

    case 'youtube':
      return {
        channel_title: metadata.channel_title || '',
        channel_id: metadata.channel_id || '',
        scopes: metadata.scopes || '',
        vault_id: metadata.vault_id,
        access_token_vault_id: metadata.access_token_vault_id,
        refresh_token_vault_id: metadata.refresh_token_vault_id,
        token_expires_at: metadata.token_expires_at,
        ...metadata,
      } as T;

    case 'pinterest':
      return {
        pinterest_user_id: metadata.pinterest_user_id || '',
        username: metadata.username || '',
        display_name: metadata.display_name || '',
        scopes: metadata.scopes || '',
        vault_id: metadata.vault_id,
        access_token_vault_id: metadata.access_token_vault_id,
        refresh_token_vault_id: metadata.refresh_token_vault_id,
        token_expires_at: metadata.token_expires_at,
        refresh_token_expires_at: metadata.refresh_token_expires_at,
        ...metadata,
      } as T;

    default:
      return {
        scopes: metadata.scopes || '',
        vault_id: metadata.vault_id,
        access_token_vault_id: metadata.access_token_vault_id,
        refresh_token_vault_id: metadata.refresh_token_vault_id,
        token_expires_at: metadata.token_expires_at,
        ...metadata,
      } as T;
  }
}

export function extractConnectionData<T extends PlatformConnectionData>(
  data: any,
  platform: string,
): T {
  const connectionData = (data?.connection_data as any) || {};

  // Provide platform-specific defaults
  switch (platform) {
    case 'shopify':
      return {
        shop_name: connectionData.shop_name || '',
        domain: connectionData.domain || '',
        myshopify_domain: connectionData.myshopify_domain || '',
        currency: connectionData.currency || 'USD',
        timezone: connectionData.timezone || '',
        ...connectionData,
      } as T;

    case 'facebook':
      return {
        page_name: connectionData.page_name || '',
        followers_count: connectionData.followers_count || 0,
        fan_count: connectionData.fan_count || 0,
        ...connectionData,
      } as T;

    case 'instagram':
      return {
        username: connectionData.username || '',
        followers_count: connectionData.followers_count || 0,
        follows_count: connectionData.follows_count || 0,
        media_count: connectionData.media_count || 0,
        ...connectionData,
      } as T;

    case 'linkedin':
      return {
        organization_name: connectionData.organization_name || '',
        industry: connectionData.industry || '',
        follower_count: connectionData.follower_count || 0,
        ...connectionData,
      } as T;

    case 'tiktok':
      return {
        display_name: connectionData.display_name || '',
        username: connectionData.username || '',
        follower_count: connectionData.follower_count || 0,
        ...connectionData,
      } as T;

    case 'google_analytics':
    case 'google-analytics':
      return {
        property_name: connectionData.property_name || '',
        account_name: connectionData.account_name || '',
        website_url: connectionData.website_url || '',
        ...connectionData,
      } as T;

    case 'youtube':
      return {
        channel_title: connectionData.channel_title || '',
        channel_id: connectionData.channel_id || '',
        profile_image_url: connectionData.profile_image_url || '',
        subscriber_count: connectionData.subscriber_count || 0,
        video_count: connectionData.video_count || 0,
        view_count: connectionData.view_count || 0,
        ...connectionData,
      } as T;

    case 'pinterest':
      return {
        pinterest_user_id: connectionData.pinterest_user_id || '',
        username: connectionData.username || '',
        display_name: connectionData.display_name || '',
        profile_image_url: connectionData.profile_image_url || '',
        board_count: connectionData.board_count || 0,
        pin_count: connectionData.pin_count || 0,
        follower_count: connectionData.follower_count || 0,
        ...connectionData,
      } as T;

    default:
      return connectionData as T;
  }
}

// Legacy compatibility types for migration period
export interface LegacyShopifyConnection {
  id: string;
  myshopify_domain: string;
  name: string;
  connected_at: string;
  status: 'active' | 'inactive';
  scopes: string;
}

// Migration helper to convert user_connections to legacy format
export function toLegacyShopifyConnection(connection: UserConnection): LegacyShopifyConnection {
  const metadata = extractMetadata<ShopifyConnectionMetadata>(connection, 'shopify');
  const connectionData = extractConnectionData<ShopifyConnectionData>(connection, 'shopify');

  return {
    id: connection.id,
    myshopify_domain: connection.platform_user_id,
    name: metadata.shop_name || connectionData.shop_name || connection.platform_user_id,
    connected_at: connection.connected_at,
    status: connection.is_active ? 'active' : 'inactive',
    scopes: metadata.scopes || '',
  };
}
